import { create } from "zustand";
import { persist } from "zustand/middleware";

import { DASHCAM_CAMERAS, type DashcamCamera } from "../../services/dashcam";

export const DASHCAM_VIEW_MODES = ["both", ...DASHCAM_CAMERAS] as const;

export type DashcamViewMode = (typeof DASHCAM_VIEW_MODES)[number];

interface DashcamViewStore {
  dmsEnabled: boolean;
  viewMode: DashcamViewMode;
  /**
   * Unidad para la que se pidió abrir la cámara desde fuera del mapa.
   *
   * La tarjeta de la unidad vive en la barra lateral y el reproductor en el
   * mapa: son dos árboles de React distintos y no comparten estado local. Este
   * campo es el recado entre los dos — la lista lo pone, el mapa lo consume y
   * lo limpia.
   *
   * No se persiste: al recargar la página nadie pidió abrir nada, y recuperar
   * la petición haría saltar el reproductor solo.
   */
  requestedDeviceId: string | null;
  setDmsEnabled: (enabled: boolean) => void;
  setViewMode: (mode: DashcamViewMode) => void;
  requestCamera: (deviceId: string) => void;
  clearCameraRequest: () => void;
}

export const useDashcamViewStore = create<DashcamViewStore>()(
  persist(
    (set) => ({
      dmsEnabled: true,
      viewMode: "both",
      requestedDeviceId: null,
      setDmsEnabled: (dmsEnabled) => set({ dmsEnabled }),
      setViewMode: (viewMode) => set({ viewMode }),
      requestCamera: (requestedDeviceId) => set({ requestedDeviceId }),
      clearCameraRequest: () => set({ requestedDeviceId: null }),
    }),
    {
      name: "wayak-dashcam-view",
      // Solo las preferencias. `requestedDeviceId` es una petición de un
      // momento, no una preferencia.
      partialize: (state) => ({ dmsEnabled: state.dmsEnabled, viewMode: state.viewMode }),
    },
  ),
);

/**
 * Qué cámaras se pintan.
 *
 * `dmsEnabled` aquí es la PREFERENCIA del usuario (persistida, default true),
 * no la capacidad del equipo. La capacidad ya viene filtrada en `available`:
 * si la unidad no trae cámara de cabina, "dms" nunca llega a esta función.
 *
 * El orden importa —capacidad primero, preferencia después— porque al revés
 * una unidad sin hardware ofrecería el panel de conductor sólo porque el
 * usuario lo dejó encendido en otra unidad que sí lo tenía.
 */
export const visibleDashcamCameras = (
  available: DashcamCamera[],
  mode: DashcamViewMode,
  dmsEnabled: boolean,
): DashcamCamera[] => {
  const allowed = available.filter((camera) => camera !== "dms" || dmsEnabled);
  return mode === "both" ? allowed : allowed.filter((camera) => camera === mode);
};
